import { useEffect, useRef } from "react"

import { isParadiseProdEnv, isParadiseStagingEnv } from "src/constants/env"
import { useAppData } from "src/context/useAppData"
import { usePostTrackEvent } from "src/data/analytics/queries/analyticsQueries"
import { getImpersonateActive } from "src/data/auth/authStorage"
import { useRouter } from "src/router/useRouter"
import { EngagementService } from "src/utils/EngagementService"
import { logger } from "src/utils/logger"

const TIMEOUT = 3000

/**
 * Provider component that handles user engagement tracking and analytics.
 *
 * This component initializes engagements for authenticated users and handles:
 * - One-time boot of engagement service per user
 * - Tracking of engagement events through analytics
 * - Navigation changes triggered by engagements
 * - Delayed initialization to ensure page load
 */
export function EngagementProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { navigate } = useRouter()
  const trackEvent = usePostTrackEvent()
  const { user, ready } = useAppData()

  const prevUserId = useRef<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const isInParadise = isParadiseProdEnv || isParadiseStagingEnv
  const isImpersonating = getImpersonateActive()

  useEffect(() => {
    if (isInParadise) {
      logger.log(
        "Skipping booting engagement service because user is in paradise"
      )
      return
    }

    if (isImpersonating) {
      logger.log(
        "Skipping booting engagement service because user is impersonating"
      )
      return
    }

    if (user?.user_id && ready) {
      /**
       *  Only run logic when a new user logs in.is
       */
      if (prevUserId.current === user.user_id) {
        return
      }

      /**
       * Make sure that we give the user some time to load the page before we
       * start showing them engagements.
       *
       * This is to prevent engagement events from being fired when the user
       * hasn't had a chance to see the page yet.
       */
      timeoutRef.current = setTimeout(() => {
        EngagementService.boot({
          userId: user.user_id,
          onEngagementEvent: (event) => {
            trackEvent.mutate({
              event: event.eventType,
              properties: event.eventProperties,
            })
          },
          onRouteChange: navigate,
        })

        prevUserId.current = user.user_id
      }, TIMEOUT)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    user?.user_id,
    trackEvent,
    navigate,
    ready,
    isInParadise,
    isImpersonating,
  ])

  return <>{children}</>
}
